// ==================================================
// FILE: themes/archboundStory.js
// ==================================================

// NARRATIVE CONTENT LIBRARY - Story arcs, chapters, and thematic beats
// Pure content for the 4-chapter campaign narrative of humanity's confrontation with the Elder Gods
// The game engine sequences these and triggers them based on campaign progression

/**
 * CHAPTER 1: THE AWAKENING
 * Days 1-30 (Approximate)
 * Theme: Discovery, containment, understanding the impossible
 * Players establish their command center, recruit first operatives, understand the Eldritch threat
 * Story Goal: Reach "First Incursion Response" mission - prove humanity can fight the Spawn
 */
export const CHAPTER_1 = {
  id: 'chapter_1',
  title: 'The Awakening',
  subtitle: 'When Gods Stir from Sleep',
  description: 'An archaeologist has deciphered an ancient text and activated the Archway. The portal is open. Spawn parasites emerge. And deep in the cosmos, something ancient takes notice of Earth once more.',
  
  theme: {
    tone: 'mysterious_dread',
    color: 'purples_and_blacks',
    pacing: 'tense_discovery'
  },
  
  openingNarrative: 'It began with a discovery. An archaeologist, brilliant and driven, found what governments had buried for sixty years. A Nazi excavation site. A minor Archway, sealed and forgotten beneath a U.S. military base. She translated the glyphs. She understood the ritual. And in her excitement to unlock humanity\'s past, she opened a door that was never meant to be opened. The Archway activated. Spawn entities emerged—parasitic horrors from beyond our reality. And across the dimensional void, the Elder Gods—beings that once enslaved all of humanity—turned their infinite attention back to Earth. You lead the military response team. Your orders: contain the threat. Your reality: nothing can prepare you for what you\'ve just discovered.',
  
  storyBeats: [
    {
      id: 'ch1_beat_1',
      day: 1,
      title: 'First Contact',
      description: 'The Archway pulses with alien energy. Your first operatives arrive at the containment site. The air itself feels wrong here.',
      narrative: 'This is not a military operation. This is first contact with something that predates civilization. The Arch hums with purpose we cannot comprehend. And from its surface, things emerge that should not exist.',
      eventHooks: ['arch_resonance', 'spawn_mutation'],
      characterSpeech: {
        protagonist: 'Secure the perimeter. No one enters or exits without my authorization. And for God\'s sake, don\'t touch anything.'
      }
    },
    {
      id: 'ch1_beat_2',
      day: 7,
      title: 'The Spawn Emerge',
      description: 'First encounter with Spawn parasites. They\'re intelligent. Adaptive. And they seek human hosts with terrifying efficiency.',
      narrative: 'These are not animals. They hunt with purpose. They learn from each encounter. Whatever created them understood biology in ways we\'re only beginning to grasp.',
      eventHooks: ['spawn_incursion', 'spawn_mutation'],
      characterSpeech: {
        xenobiologist: 'These organisms shouldn\'t exist. Their cellular structure violates known physics.',
        protagonist: 'They exist. And they\'re killing my people. That\'s all I need to know.'
      }
    },
    {
      id: 'ch1_beat_3',
      day: 14,
      title: 'Atlantean Records',
      description: 'Linguistic team deciphers inscriptions around the Archway. They reference Atlantis, enslavement, and "gods among us."',
      narrative: 'The myths were real. Atlantis existed. The gods existed. And humanity was their property across countless worlds. This is not a new war. This is an old war, awakening.',
      eventHooks: ['atlantean_whispers', 'sumerian_tablets'],
      characterSpeech: {
        archaeologist: 'Every ancient civilization worshiped the same gods. Different names. Same beings. They were real.',
        protagonist: 'Then we\'re fighting gods. Great. Just great.'
      }
    },
    {
      id: 'ch1_beat_4',
      day: 21,
      title: 'First Containment Victory',
      description: 'Your team successfully neutralizes a Spawn nest. Proof that these things can be killed. Humanity is not defenseless.',
      narrative: 'We can fight them. The impossible can bleed. The unknowable can die. This is the moment we stop being victims and start being soldiers.',
      eventHooks: ['expedition_success'],
      missionTrigger: 'first_incursion_response',
      characterSpeech: {
        protagonist: 'Lock down the site. Catalog everything. This is our first victory, but it won\'t be our last.',
        operative: 'Sir, we just killed something that shouldn\'t exist. That changes everything.'
      }
    }
  ],
  
  thematicElements: [
    'Discovery of cosmic horror',
    'Humanity confronting the impossible',
    'Learning to fight what was once gods',
    'Establishing containment as possible',
    'The weight of ancient secrets'
  ],
  
  eventPool: ['backdrop', 'threat_low', 'morale_minor'],
  
  failureState: {
    title: 'Containment Breach',
    description: 'If casualty rate exceeds 40%, the site is evacuated and quarantined. Operations cease.',
    narrative: 'We couldn\'t hold it. The Spawn spread beyond our perimeter. Now we run, seal the site, and pray they don\'t follow.'
  },
  
  successState: {
    title: 'The Gate Holds',
    description: 'First containment succeeds. Command authorizes expanded operations. You\'re no longer just responding—you\'re hunting.',
    narrative: 'We held the line. The Archway remains contained. The Spawn are catalogued. And for the first time, we believe we might survive this.'
  }
};

/**
 * CHAPTER 2: THE NETWORK EXPANDS
 * Days 31-70 (Approximate)
 * Theme: Growth, international cooperation, uncovering the Arch Network
 * Players expand operations, recruit more operatives, coordinate with other nations
 * Story Goal: Reach "Global Coordination" mission - unite international Arch sites
 */
export const CHAPTER_2 = {
  id: 'chapter_2',
  title: 'The Network Expands',
  subtitle: 'When All Doors Open',
  description: 'Your success draws international attention. Other nations reveal their own dormant Archways—buried in Egypt, hidden in Mesoamerica, sealed beneath Sumerian ruins. The Arch Network is global. And it\'s all waking up.',
  
  theme: {
    tone: 'expanding_dread',
    color: 'indigos_and_golds',
    pacing: 'accelerating'
  },
  
  openingNarrative: 'The activation of one Archway triggered a cascade. Dormant portals buried for millennia pulse with renewed energy. Egypt admits to finding one beneath the Sphinx. Mexico reveals inscriptions in Teotihuacan matching your site. Iraq unseals classified files on Sumerian excavations. The Arch Network—the ancient system the gods built to enslave worlds—is reactivating. And with each portal that awakens, more Spawn emerge. Humanity must cooperate or face extinction separately. But cooperation has never been humanity\'s strength. Now you must convince nations with conflicting interests that the threat from beyond is more important than any earthly conflict.',
  
  storyBeats: [
    {
      id: 'ch2_beat_1',
      day: 31,
      title: 'Global Revelation',
      description: 'Classified briefings reveal the scope: dozens of Archway sites worldwide, all showing signs of activation.',
      narrative: 'We thought we were containing one anomaly. We were wrong. The entire planet is riddled with these portals. Atlantis built a network that spans continents. And now it\'s all coming back online.',
      eventHooks: ['allied_site', 'arch_resonance'],
      characterSpeech: {
        intelligence_officer: 'Every site we\'ve identified is showing the same energy signature. They\'re linked. All of them.',
        protagonist: 'Then we need to link our response. If they operate as a network, so do we.'
      }
    },
    {
      id: 'ch2_beat_2',
      day: 42,
      title: 'Atlantean Technology Recovered',
      description: 'Deep excavation reveals intact Atlantean artifacts. Technology that bends physics. Weapons designed to fight gods.',
      narrative: 'Atlantis fell, but they left weapons behind. Tools forged in a war against cosmic tyranny. Now those tools are ours.',
      eventHooks: ['artifact_cache', 'atlantean_library'],
      characterSpeech: {
        scientist: 'This technology is millennia beyond our understanding. But they built it to fight the same enemy we face.',
        protagonist: 'Then we learn how to use it. Fast.'
      }
    },
    {
      id: 'ch2_beat_3',
      day: 54,
      title: 'International Tensions',
      description: 'Nations compete for control of Arch sites. Some want to weaponize the portals. Others want them destroyed. Trust fractures.',
      narrative: 'Humanity faces extinction from beyond the stars, and we\'re still fighting each other over borders and ideology. The gods must be laughing.',
      eventHooks: ['nation_demands', 'black_market_occult'],
      characterSpeech: {
        diplomat: 'Three countries are threatening unilateral action. If we don\'t establish unified protocols, this becomes a geopolitical nightmare.',
        protagonist: 'Then we make them understand: there won\'t be any geopolitics if the gods return.'
      }
    },
    {
      id: 'ch2_beat_4',
      day: 66,
      title: 'The Global Pact',
      description: 'After tense negotiations, international coalition forms. All Arch sites agree to coordinate operations and share intelligence.',
      narrative: 'For the first time in history, humanity stands together not because of shared values, but shared fear. It\'s not ideal. But it\'s enough.',
      eventHooks: ['research_partnership', 'international_alliance'],
      missionTrigger: 'global_coordination_strike',
      characterSpeech: {
        protagonist: 'Ladies and gentlemen, we are now a unified command. Every site, every nation, one mission: keep the gods from returning.'
      }
    }
  ],
  
  thematicElements: [
    'Global scale of the threat',
    'International cooperation under duress',
    'Atlantean legacy as weapon and warning',
    'Humanity learning from ancient failures',
    'The burden of unified command'
  ],
  
  eventPool: ['backdrop', 'threat_medium', 'morale_mixed', 'faction', 'discovery'],
  
  failureState: {
    title: 'Coalition Collapse',
    description: 'If coordination mission fails or nations turn against each other, sites operate independently. Spawn spread unchecked.',
    narrative: 'We failed to unite. Each nation retreats to its own borders, its own solutions. The Arch Network expands while humanity fragments.'
  },
  
  successState: {
    title: 'United Earth Command',
    description: 'Coalition succeeds. Global intelligence network established. Humanity fights as one species against cosmic threat.',
    narrative: 'We did it. Every Archway site linked. Every nation coordinating. For the first time, Earth has a unified defense against the cosmos.'
  }
};

/**
 * CHAPTER 3: THE GODS RETURN
 * Days 71-110 (Approximate)
 * Theme: Cosmic horror, overwhelming power, testing of sanity and resolve
 * Eldritch entities begin manifesting directly. Cultists emerge. Reality itself becomes unstable.
 * Story Goal: Reach "The Binding Ritual" mission - use Atlantean knowledge to push back divine incursion
 */
export const CHAPTER_3 = {
  id: 'chapter_3',
  title: 'The Gods Return',
  subtitle: 'When Infinity Takes Notice',
  description: 'The Spawn were merely scouts. Parasites sent ahead to prepare the way. Now, through the fully active Arch Network, the Elder Gods themselves reach into our reality. Beings of impossible scale and power. And they remember humanity as property.',
  
  theme: {
    tone: 'apocalyptic_dread',
    color: 'blacks_and_crimsons',
    pacing: 'overwhelming'
  },
  
  openingNarrative: 'It started with the dreams. Every operative, every researcher, everyone near an Archway began having the same visions. Vast consciousnesses pressing against the barriers of reality. Ancient intelligence that spans galaxies. The Elder Gods are no longer dormant. They reach through the Arch Network, and where they touch, reality fractures. Cultists emerge—humans who worship the returning gods and seek to hasten their arrival. Dimensional barriers weaken. Spawn evolution accelerates. And through every Archway, something unspeakably vast begins to push through. You realize now that all previous operations were merely preparation. This is the true war: humanity versus beings that regard us as cattle they once owned.',
  
  storyBeats: [
    {
      id: 'ch3_beat_1',
      day: 71,
      title: 'Divine Manifestation',
      description: 'An Elder God\'s presence manifests partially through the primary Archway. Reality warps. Operatives experience psychic trauma.',
      narrative: 'We saw it. Not all of it—human minds cannot comprehend the totality—but enough. Enough to understand that we are insects trying to fight the ocean.',
      eventHooks: ['psychic_assault', 'god_transmission'],
      characterSpeech: {
        operative: 'I looked at it. I can\'t... I can\'t unsee it. It knows we\'re here. It knows our names.',
        protagonist: 'We hold the line. I don\'t care what it is or what it knows. We hold.'
      }
    },
    {
      id: 'ch3_beat_2',
      day: 83,
      title: 'The Cult Emerges',
      description: 'Massive cult network revealed within operation. They\'ve been sabotaging containment, accelerating Arch activation.',
      narrative: 'Some humans see gods returning and fall to their knees. They remember millennia of slavery and call it peace. They are as dangerous as the gods themselves.',
      eventHooks: ['cultist_infiltration', 'cultist_sabotage'],
      characterSpeech: {
        cultist: 'You fight the inevitable. The gods are eternal. They offer us purpose.',
        protagonist: 'They offer you chains. And we\'ll fight them and anyone who serves them.'
      }
    },
    {
      id: 'ch3_beat_3',
      day: 95,
      title: 'Reality Fractures',
      description: 'Multiple Archways destabilize simultaneously. Dimensional barriers collapse. Impossible geometries manifest in physical space.',
      narrative: 'The walls between worlds are paper thin. The gods push from beyond, and reality tears. Physics becomes optional. Madness becomes tangible.',
      eventHooks: ['arch_expansion', 'equipment_corruption', 'morale_breakdown'],
      characterSpeech: {
        scientist: 'The laws of physics are breaking down. Space and time are becoming... negotiable.',
        protagonist: 'Then we adapt. If reality is breaking, we learn to fight in broken reality.'
      },
      missionTrigger: 'binding_ritual_deployment'
    },
    {
      id: 'ch3_beat_4',
      day: 107,
      title: 'The Atlantean Solution',
      description: 'Ancient texts reveal the Binding—ritual Atlantis used to temporarily sever gods from our dimension. It requires sacrifice.',
      narrative: 'Atlantis didn\'t win their war. But they bought time. They bound the gods and sent them into dormancy. Now we must do the same. And we know the cost.',
      eventHooks: ['binding_ritual', 'atlantean_library'],
      characterSpeech: {
        protagonist: 'Atlantis fell performing this ritual. But they saved humanity for ten thousand years. That\'s a legacy worth dying for.'
      }
    }
  ],
  
  thematicElements: [
    'Confronting cosmic scale entities',
    'Sacrifice for greater good',
    'Human cultists as traitors to species',
    'Sanity as limited resource',
    'Using ancient knowledge against ancient evil',
    'Reality as negotiable concept'
  ],
  
  eventPool: ['threat_high', 'morale_crisis', 'faction_conflict', 'campaign_catastrophe'],
  
  failureState: {
    title: 'Dimensional Collapse',
    description: 'If the binding ritual fails, the gods fully manifest. Earth becomes their domain once more.',
    narrative: 'We tried. We fought with everything we had. But infinity cannot be contained. The gods return. The age of human freedom ends.'
  },
  
  successState: {
    title: 'The Binding Holds',
    description: 'The ritual succeeds. Divine manifestations recede. Archways stabilize. Humanity buys itself time to prepare.',
    narrative: 'We pushed them back. Not forever—never forever. But we earned breathing room. The gods retreat into dormancy. And in that dormancy, we find hope.'
  }
};

/**
 * CHAPTER 4: THE GODKILLER
 * Days 111-150 (Approximate)
 * Theme: Offensive warfare, turning ancient knowledge into weapons, decisive strike
 * Players discover method to permanently destroy an Elder God and execute final operation
 * Story Goal: Reach "The Godkiller Strike" mission - eliminate an Elder God permanently
 */
export const CHAPTER_4 = {
  id: 'chapter_4',
  title: 'The Godkiller',
  subtitle: 'When Mortals Become Legend',
  description: 'The Binding bought time. But time is meaningless to immortal beings. The gods will return. Unless humanity does what Atlantis never could: kill a god permanently. The ancient texts speak of weapons. Of rituals. Of impossible victories. Now, armed with Atlantean technology and desperate courage, humanity prepares its most audacious operation: deicide.',
  
  theme: {
    tone: 'determined_mythic',
    color: 'golds_and_violets',
    pacing: 'climactic'
  },
  
  openingNarrative: 'Atlantis bound the gods. They sent them into sleep. But they could never kill them. The Elder Gods are older than stars, vaster than galaxies, existing across dimensions humanity barely comprehends. To kill such a being should be impossible. But buried in Atlantean archives, your researchers discovered something. Not a weapon, but a possibility. A way to channel the combined Arch Network against itself. To weaponize the very portals the gods use to reach us. It will require every operational site working in perfect synchronization. It will demand sacrifice. And success is far from guaranteed. But for the first time since the Awakening, humanity isn\'t just surviving. Humanity is hunting.',
  
  storyBeats: [
    {
      id: 'ch4_beat_1',
      day: 111,
      title: 'The Godkiller Weapon',
      description: 'Deep sea expedition recovers Atlantean superweapon. Technology designed specifically to kill Elder Gods. It was never completed.',
      narrative: 'Atlantis fell before they could finish it. But they left blueprints, components, and most importantly—proof that deicide is possible.',
      eventHooks: ['atlantean_weapon', 'binding_ritual'],
      characterSpeech: {
        engineer: 'This is beyond anything we\'ve ever attempted. But the theory is sound. We can weaponize the entire Arch Network.',
        protagonist: 'Then we complete what Atlantis started. We finish the weapon. And we use it.'
      }
    },
    {
      id: 'ch4_beat_2',
      day: 123,
      title: 'Divine Coordinates',
      description: 'Dimensional mapping successfully triangulates location of a dormant Elder God. A target is identified.',
      narrative: 'We found one. Not all of them—there are more gods than humanity has names for—but one. Asleep. Vulnerable. As vulnerable as infinity can be.',
      eventHooks: ['god_location'],
      characterSpeech: {
        scientist: 'The calculations are confirmed. We know where it sleeps. We can strike at its core.',
        protagonist: 'Then we strike. And we don\'t miss.'
      }
    },
    {
      id: 'ch4_beat_3',
      day: 135,
      title: 'Global Synchronization',
      description: 'Every Arch site worldwide coordinates for single simultaneous operation. All of humanity\'s resources focused on one impossible goal.',
      narrative: 'Every nation. Every operative. Every researcher. Every Archway. All acting as one. This is humanity at its finest—unified, desperate, and audacious.',
      eventHooks: ['humanity_united'],
      characterSpeech: {
        protagonist: 'All stations, this is Command. Final authorization codes transmitted. On my mark, we change history. We kill a god.'
      }
    },
    {
      id: 'ch4_beat_4',
      day: 147,
      title: 'The Godkiller Strike',
      description: 'The operation begins. The Arch Network weaponized. Reality tears. And humanity reaches across dimensions to strike at infinity.',
      narrative: 'This is the moment. Not just for us, but for every human who ever lived under divine tyranny. For Atlantis. For all the lost worlds. We strike not with hope, but with certainty. We are humanity. And we refuse to kneel.',
      eventHooks: [],
      missionTrigger: 'final_godkiller_strike',
      characterSpeech: {
        protagonist: 'Fire the weapon. All sites. Now. Let them know: humanity will never be property again.'
      }
    }
  ],
  
  thematicElements: [
    'Humanity ascending to myth',
    'Using oppressor\'s tools against them',
    'Completing Atlantis\' legacy',
    'Impossible made possible through unity',
    'Mortals killing gods',
    'Defining human freedom through action'
  ],
  
  eventPool: ['morale_triumph', 'discovery_victory', 'campaign_victory'],
  
  failureState: {
    title: 'The God Endures',
    description: 'If the strike fails, the Elder God awakens fully. Retaliation is absolute. Earth becomes a harvested world.',
    narrative: 'We reached for godhood. We grasped too far. Now the gods awaken, and their vengeance knows no mercy. We are harvested. We are extinct.'
  },
  
  successState: {
    title: 'Deicide Accomplished',
    description: 'The Elder God dies. Its death echoes across dimensions. Other gods feel it. Humanity has proven it can kill the infinite.',
    narrative: 'We did it. An Elder God—a being older than Earth itself—is dead. Truly, permanently dead. The other gods felt it die. They fear us now. And in that fear, humanity finds its greatest weapon: proof that we are no one\'s cattle anymore.'
  }
};

/**
 * STORY THREADS
 * Ongoing narrative elements that can be triggered throughout the chapters
 * These give depth and allow for player choice/exploration
 */
export const STORY_THREADS = {
  atlantean_rebellion: {
    id: 'atlantean_rebellion',
    title: 'Echoes of Atlantis',
    description: 'Uncovering the truth about humanity\'s first war against the gods',
    chapters: ['chapter_1', 'chapter_2', 'chapter_4'],
    keyEvents: ['atlantean_hero', 'atlantean_library', 'atlantean_weapon']
  },
  
  divine_contact: {
    id: 'divine_contact',
    title: 'Communion with Infinity',
    description: 'Direct communication with Elder Gods and understanding their perspective',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3'],
    keyEvents: ['god_transmission', 'psychic_assault', 'divine_ultimatum']
  },
  
  spawn_adaptation: {
    id: 'spawn_adaptation',
    title: 'The Servant Species',
    description: 'Understanding and weaponizing knowledge of Spawn biology',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3'],
    keyEvents: ['spawn_mutation', 'spawn_incursion', 'containment_breakthrough']
  },
  
  cult_activity: {
    id: 'cult_activity',
    title: 'The Faithful Betrayers',
    description: 'Confronting humans who worship and serve the returning gods',
    chapters: ['chapter_2', 'chapter_3'],
    keyEvents: ['cultist_infiltration', 'cultist_sabotage', 'black_market_occult']
  },
  
  global_network: {
    id: 'global_network',
    title: 'United Earth Command',
    description: 'Building and maintaining international cooperation against cosmic threat',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['allied_site', 'international_alliance', 'humanity_united']
  },
  
  divine_weakness: {
    id: 'divine_weakness',
    title: 'The Flaw in Infinity',
    description: 'Discovering that Elder Gods, despite their power, can be fought and killed',
    chapters: ['chapter_3', 'chapter_4'],
    keyEvents: ['divine_weakness', 'binding_ritual', 'god_location']
  }
};

/**
 * KEY MISSIONS - Story trigger points
 * These are defined here as reference; actual mission details live elsewhere
 * But these mark the narrative progression points
 */
export const KEY_MISSIONS = [
  { id: 'first_incursion_response', chapter: 'chapter_1', day: '~21' },
  { id: 'global_coordination_strike', chapter: 'chapter_2', day: '~66' },
  { id: 'binding_ritual_deployment', chapter: 'chapter_3', day: '~95' },
  { id: 'final_godkiller_strike', chapter: 'chapter_4', day: '~147' }
];

const archboundStoryLibrary = {
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  CHAPTER_4,
  STORY_THREADS,
  KEY_MISSIONS
};

export default archboundStoryLibrary;